import React, { useState, useEffect } from "react";
import { Checkbox, TextField, Button } from "@mui/material";
import axiosInstance from "../../../api/axiosInstance";

export default function TagDialog({
  open,
  onClose,
  onConfirm,
  existingTags = [],
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [closeFriends, setCloseFriends] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selected, setSelected] = useState(existingTags);

  /* ------------------------------------------------------------------ */
  /*  Data fetching & state sync                                         */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    (async () => {
      try {
        const resp = await axiosInstance.get("/flashes/close-users");
        setCloseFriends(resp.data.users || []);
      } catch (err) {
        console.error(err);
      }
    })();
  }, []);

  useEffect(() => {
    setSelected(existingTags);
  }, [existingTags]);

  const handleSearch = async (val) => {
    setSearchTerm(val);
    if (!val) {
      setFiltered([]);
      return;
    }
    try {
      const resp = await axiosInstance.get("/flashes/search-users", {
        params: { query: val },
      });
      setFiltered(resp.data.users || []);
    } catch (err) {
      console.error(err);
    }
  };

  const displayed = searchTerm
    ? filtered.filter(
        (u) => !existingTags.some((t) => t.user_id === u.user_id)
      )
    : closeFriends.filter(
        (u) => !existingTags.some((t) => t.user_id === u.user_id)
      );

  const toggleSelect = (u) =>
    setSelected((prev) =>
      prev.find((x) => x.user_id === u.user_id)
        ? prev.filter((x) => x.user_id !== u.user_id)
        : [...prev, u]
    );

  const handleConfirm = () =>
    onConfirm(
      selected.map((u) => u.user_id),
      selected
    );

  /* ------------------------------------------------------------------ */
  /*  Portal markup                                                      */
  /* ------------------------------------------------------------------ */
  if (!open) return null;

  return (
    <div id='Dialog'
    className="fixed inset-0 flex z-100000 items-center justify-center"
      >
      <div
        className="relative w-11/12 z-100000 max-w-md rounded-xl bg-white dark:bg-smallDarkCard p-6 shadow-xl dark:text-gray-100"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-center text-xl font-bold text-black dark:text-white">Tag users</h2>

        {/* ── Search field ─────────────────────────────────────────── */}
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search users…"
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{
            backgroundColor: "#f3f3f3",
            borderRadius: "8px",
            // Light mode
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#e5e7eb" },
              "&:hover fieldset": { borderColor: "#d1d5db" },
              "&.Mui-focused fieldset": { borderColor: "#94a3b8" },
            },
            "& .MuiInputBase-input": {
              color: "#111827",
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#6b7280",
              opacity: 1,
            },
            // Dark mode
            ".dark &": {
              backgroundColor: "#101010",
            },
            ".dark & .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "#101010" },
              "&.Mui-focused fieldset": { borderColor: "#48c9b0" },
            },
            ".dark & .MuiInputBase-input": {
              color: "white",
            },
            ".dark & .MuiInputBase-input::placeholder": {
              color: "#d1d5db",
              opacity: 1,
            },
          }}
        />

        {/* ── Users list (search or close friends) ─────────────────── */}
        <div className="mt-4 max-h-40 overflow-y-auto space-y-1">
          {displayed.map((user) => (
            <label
              key={user.user_id}
              className="flex items-center gap-2 rounded-md p-2 hover:bg-gray-100 dark:hover:bg-zinc-800"
            >
              <Checkbox
                checked={selected.some((x) => x.user_id === user.user_id)}
                onChange={() => toggleSelect(user)}
                sx={{
                  ".dark &": {
                    color: "#d1d5db",
                  },
                  "&.Mui-checked": {
                    color: "#48c9b0",
                  },
                  ".dark &.Mui-checked": {
                    color: "#48c9b0",
                  },
                }}
              />
              <div className="flex items-center gap-2">
                <img
                  src={user.profile_url || "/assets/images/default_profile.png"}
                  alt={user.username}
                  className="w-6 h-6 rounded-full object-cover"
                />
                <span className="text-black dark:text-white">{user.username}</span>
              </div>
            </label>
          ))}
        </div>

        {/* ── Already-selected users (incl. existingTags) ──────────── */}
        {selected.length > 0 && (
          <div className="mt-4">
            <h3 className="mb-2 text-sm font-medium">
              <span className="text-gray-600 dark:text-gray-300">Selected</span>
            </h3>
            <div className="max-h-28 overflow-y-auto space-y-1">
              {selected.map((user) => (
                <label
                  key={user.user_id}
                  className="flex items-center gap-2 rounded-md p-2"
                >
                  <Checkbox
                    checked
                    onChange={() => toggleSelect(user)}
                    sx={{
                      ".dark &": { color: "#d1d5db" },
                      "&.Mui-checked": { color: "#48c9b0" },
                      ".dark &.Mui-checked": { color: "#48c9b0" },
                    }}
                  />
                  <div className="flex items-center gap-2">
                    <img
                      src={user.profile_url || "/assets/images/default_profile.png"}
                      alt={user.username}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span className="text-black dark:text-white">{user.username}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* ── Actions ──────────────────────────────────────────────── */}
        <div className="mt-6 flex justify-between">
          <Button
            onClick={onClose}
            sx={{
              color: "#000",
              textTransform: "none",
              borderRadius: "8px",
              ".dark &": {
                color: "#fff",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            sx={{
              backgroundColor: "#2c3e50",
              color: "#1f1f1f",
              textTransform: "none",
              borderRadius: "8px",
              ".dark &": { backgroundColor: "#48c9b0" },
            }}
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}
